// =====================================================
// PAGE ADMIN: ESCALADES URGENCE TRIAGE
// =====================================================

import { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { AlertTriangle, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

export default function AdminPsyTriage() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [triageResponses, setTriageResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'urgent' | 'handled'>('urgent');

  useEffect(() => {
    loadTriageResponses();
  }, []);

  const loadTriageResponses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('psy_triage_responses')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setTriageResponses(data || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsHandled = async (id: string) => {
    try {
      const { error } = await supabase
        .from('psy_triage_responses')
        .update({ escalation_handled: true })
        .eq('id', id);

      if (error) throw error;
      toast({
        title: 'Succès',
        description: 'Marqué comme traité',
      });
      loadTriageResponses();
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive',
      });
    }
  };

  const getFilteredResponses = () => {
    switch (filter) {
      case 'urgent':
        return triageResponses.filter((r) => r.escalation_required && !r.escalation_handled);
      case 'handled':
        return triageResponses.filter((r) => r.escalation_handled);
      default:
        return triageResponses;
    }
  };

  const getSeverityBadge = (urgencyLevel: string) => {
    const badges: Record<string, { className: string; label: string }> = {
      emergency: { className: 'bg-red-400/20 text-red-400 border-red-400/20', label: 'URGENCE' },
      high: { className: 'bg-orange-400/20 text-orange-400 border-orange-400/20', label: 'Élevé' },
      moderate: { className: 'bg-yellow-400/20 text-yellow-400 border-yellow-400/20', label: 'Modéré' },
      low: { className: 'bg-green-400/20 text-green-400 border-green-400/20', label: 'Faible' },
    };

    const badge = badges[urgencyLevel] || badges.moderate;

    return (
      <Badge className={badge.className}>
        {badge.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center p-12">
          <Clock className="mr-2 h-8 w-8 animate-spin text-yellow-400" />
        </div>
      </AdminLayout>
    );
  }

  const filteredResponses = getFilteredResponses();
  const urgentCount = triageResponses.filter((r) => r.escalation_required && !r.escalation_handled).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Escalades d'Urgence</h1>
            <p className="text-gray-400">Surveillez les cas urgents détectés par le triage</p>
          </div>
          {urgentCount > 0 && (
            <div className="px-4 py-2 bg-red-400/20 text-red-400 border border-red-400/20 rounded-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {urgentCount} cas urgent{urgentCount > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total réponses</div>
            <div className="text-2xl font-bold text-white">{triageResponses.length}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-400 mb-1">Cas urgents</div>
                <div className="text-2xl font-bold text-red-400">{urgentCount}</div>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Traités</div>
            <div className="text-2xl font-bold text-green-400">
              {triageResponses.filter((r) => r.escalation_handled).length}
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 mb-6">
          <div className="flex gap-4">
            <Button
              onClick={() => setFilter('urgent')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'urgent'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              Urgents ({urgentCount})
            </Button>
            <Button
              onClick={() => setFilter('handled')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'handled'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              Traités
            </Button>
            <Button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-yellow-500 text-black hover:bg-yellow-600'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              Tous
            </Button>
          </div>
        </div>

        {/* Liste */}
        <div className="space-y-4">
          {filteredResponses.map((response) => (
            <div
              key={response.id}
              className={`bg-white/5 backdrop-blur-sm border rounded-2xl p-6 ${
                response.escalation_required && !response.escalation_handled
                  ? 'border-red-500'
                  : 'border-white/10'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  {response.escalation_required && !response.escalation_handled && (
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  )}
                  <div>
                    <div className="font-semibold text-white">
                      {response.full_questionnaire?.patient_phone || 'Anonyme'}
                    </div>
                    <div className="text-sm text-gray-400 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {new Date(response.created_at).toLocaleString('fr-FR')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getSeverityBadge(response.urgency_level)}
                  {!response.escalation_handled && response.escalation_required && (
                    <Button
                      onClick={() => markAsHandled(response.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Marquer comme traité
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Score PHQ-9</div>
                  <div className="text-lg font-bold text-white">{response.phq9_score}/27</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Score GAD-7</div>
                  <div className="text-lg font-bold text-white">{response.gad7_score}/21</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Niveau urgence</div>
                  <div className="text-lg font-bold text-white">{response.urgency_level}</div>
                </div>
              </div>

              {response.red_flags && response.red_flags.length > 0 && (
                <div className="bg-red-400/20 border border-red-400/20 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-red-400 mb-1">Drapeaux rouges détectés</div>
                      <div className="text-sm text-red-300">
                        {response.red_flags.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {response.auto_recommendation && (
                <div className="bg-blue-400/20 border border-blue-400/20 rounded-lg p-4 mb-4">
                  <div className="font-semibold text-blue-400 mb-2">Recommandation</div>
                  <div className="text-sm text-blue-300">{response.auto_recommendation}</div>
                </div>
              )}

              {response.full_questionnaire?.patient_phone && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Phone className="w-4 h-4" />
                      {response.full_questionnaire.patient_phone}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {filteredResponses.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <AlertTriangle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p>Aucune réponse trouvée</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
